/**
 * Rifle aim-offset sampler. Bilinearly blends the baked UE aim-offset grid
 * (aimOffsetData.ts) at a continuous (yaw, pitch) and writes one LOCAL-space
 * quaternion delta per torso bone — the avatar layers these over the locomotion
 * pose so the RMB-aiming character bends + looks along the aim (avatar.ts
 * applyAimOffset). See client/test/bake-aimoffset.mts for how the grid is baked,
 * and measure-aimoffset.mts for the axis semantics (X=yaw, Y=pitch, +Y=up).
 */
import * as THREE from "three";
import {
  AIM_OFFSET_BONES,
  AIM_OFFSET_ARM_START,
  AIM_OFFSET_YAWS,
  AIM_OFFSET_PITCHES,
  AIM_OFFSET_STAND,
  AIM_OFFSET_CROUCH,
  type AimOffsetGrid,
} from "./aimOffsetData";

export const AIM_BONE_COUNT = AIM_OFFSET_BONES.length;
export { AIM_OFFSET_ARM_START };

/** Locate `v` on a sorted axis: the two bracketing sample indices + the [0,1] fraction
 *  between them (clamped at the ends, where i0===i1 and f===0). */
function bracket(axis: readonly number[], v: number): { i0: number; i1: number; f: number } {
  const last = axis.length - 1;
  if (v <= axis[0]) return { i0: 0, i1: 0, f: 0 };
  if (v >= axis[last]) return { i0: last, i1: last, f: 0 };
  let i = 0;
  while (i < last && axis[i + 1] <= v) i++;
  return { i0: i, i1: i + 1, f: (v - axis[i]) / (axis[i + 1] - axis[i]) };
}

export class AimOffsetSampler {
  private _t0 = new THREE.Quaternion();
  private _t1 = new THREE.Quaternion();
  private _t2 = new THREE.Quaternion();
  private _t3 = new THREE.Quaternion();
  private _crouch = Array.from({ length: AIM_BONE_COUNT }, () => new THREE.Quaternion());

  /** Bilinearly sample at (yawDeg, pitchDeg), blend stand↔crouch by `crouchK`, and write
   *  the per-bone delta into out[start..end). `out` must be preallocated to AIM_BONE_COUNT.
   *  The range lets the caller sample the torso and arm blocks at DIFFERENT grid points —
   *  the torso takes the turn-lead yaw, the arms sample at yaw 0 so the held gun doesn't
   *  swing with the flourish (avatar.ts applyAimOffset). */
  sample(yawDeg: number, pitchDeg: number, crouchK: number, out: THREE.Quaternion[],
    start: number = 0, end: number = AIM_BONE_COUNT): void {
    this.sampleGrid(AIM_OFFSET_STAND, yawDeg, pitchDeg, out, start, end);
    if (crouchK > 0.01) {
      this.sampleGrid(AIM_OFFSET_CROUCH, yawDeg, pitchDeg, this._crouch, start, end);
      for (let i = start; i < end; i++) out[i].slerp(this._crouch[i], crouchK);
    }
  }

  private sampleGrid(grid: AimOffsetGrid, yawDeg: number, pitchDeg: number, out: THREE.Quaternion[],
    start: number, end: number): void {
    const y = bracket(AIM_OFFSET_YAWS, yawDeg);
    const p = bracket(AIM_OFFSET_PITCHES, pitchDeg);
    const c00 = grid.cells[p.i0][y.i0];
    const c10 = grid.cells[p.i0][y.i1];
    const c01 = grid.cells[p.i1][y.i0];
    const c11 = grid.cells[p.i1][y.i1];
    for (let i = start; i < end; i++) {
      const o = i * 4;
      read(c00, o, this._t0).slerp(read(c10, o, this._t1), y.f); // lerp yaw at pitch row 0
      read(c01, o, this._t2).slerp(read(c11, o, this._t3), y.f); // lerp yaw at pitch row 1
      out[i].copy(this._t0).slerp(this._t2, p.f); // lerp pitch
    }
  }
}

function read(flat: readonly number[], o: number, q: THREE.Quaternion): THREE.Quaternion {
  return q.set(flat[o], flat[o + 1], flat[o + 2], flat[o + 3]);
}
